import React, { ErrorInfo } from "react";
import { tracker } from "@utils/openreplay";
import { onContactUsClick } from "@utils/contactUs";
import { errorBoundaryWsp } from "@constants/index";

interface State {
  hasError: boolean;
}

interface Props {
  children: React.ReactNode;
}

export class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    tracker?.event("ErrorBoundary", {
      error: error.message,
      stack: error.stack,
      componentStack: info.componentStack,
    });
  }

  handleGoHome = () => {
    localStorage.removeItem("auth_tokens");
    window.location.href = "/";
  };

  handleContactSupport = () => {
    onContactUsClick(errorBoundaryWsp);
  };

  render() {
    if (this.state.hasError) {
      return (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            minHeight: "100vh",
            fontFamily: "'Poppins', sans-serif",
            backgroundColor: "#F5F8FB",
          }}
        >
          <header
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: 64,
              borderBottom: "1px solid rgba(0,0,0,0.08)",
              backgroundColor: "#FFFFFF",
              position: "sticky",
              top: 0,
            }}
          >
            <a href="/app/solicitudes" style={{ textDecoration: "none" }}>
              <svg
                viewBox="0 0 1199 223"
                xmlns="http://www.w3.org/2000/svg"
                style={{ width: 110, height: 20 }}
              >
                <path
                  d="M585.285 39.8165C581.041 39.8165 577.471 38.466 574.577 35.765C571.683 32.871 570.236 29.3018 570.236 25.0573C570.236 20.8128 571.683 17.34 574.577 14.6389C577.471 11.745 581.041 10.298 585.285 10.298C589.337 10.298 592.81 11.745 595.703 14.6389C598.597 17.34 600.044 20.8128 600.044 25.0573C600.044 29.3018 598.597 32.871 595.703 35.765C592.81 38.466 589.337 39.8165 585.285 39.8165ZM593.388 64.1259V218.954H576.024V64.1259H593.388Z"
                  fill="#25257F"
                />
                <path
                  d="M554.878 218.954H532.305L465.744 138.212L447.512 154.418V218.954H430.148V0.458496H447.512V133.292L524.781 64.126H548.222L477.899 127.215L554.878 218.954Z"
                  fill="#25257F"
                />
                <path
                  d="M394.331 134.739C394.331 136.861 394.042 141.395 393.463 148.34H263.234C264.584 165.897 270.758 179.981 281.755 190.592C292.752 201.011 307.512 206.22 326.033 206.22C336.066 206.22 346.291 204.58 356.709 201.3C367.128 197.827 375.81 193.293 382.755 187.698L390.858 201.011C382.948 207.184 373.109 212.297 361.34 216.349C349.764 220.4 337.899 222.426 325.744 222.426C299.119 222.426 279.054 214.516 265.549 198.695C252.044 182.682 245.291 163.485 245.291 141.105C245.291 117.761 252.333 98.4673 266.417 83.2257C280.501 67.9841 299.119 60.3633 322.271 60.3633C343.108 60.3633 360.279 67.1159 373.784 80.6211C387.482 93.9334 394.331 111.973 394.331 134.739ZM322.271 76.2802C306.065 76.2802 292.56 81.3928 281.755 91.6182C271.144 101.844 264.97 115.445 263.234 132.423H376.388C376.195 115.445 371.083 101.844 361.05 91.6182C351.211 81.3928 338.284 76.2802 322.271 76.2802Z"
                  fill="#25257F"
                />
                <path
                  d="M17.6533 218.952H0V5.08728H7.81374L101.868 143.419L195.054 5.08728H203.157V218.952H185.504V48.7864L103.894 169.755H99.5528L17.6533 49.0757V218.952Z"
                  fill="#25257F"
                />
                <path
                  d="M1124.21 222.426C1099.9 222.426 1079.84 214.709 1064.02 199.275C1048.39 184.033 1040.58 164.257 1040.58 139.948C1040.58 115.639 1048.49 95.863 1064.31 80.6214C1080.32 64.9939 1100.67 57.1802 1125.37 57.1802C1141.58 57.1802 1156.24 61.0388 1169.36 68.7561C1182.67 76.4734 1192.51 87.2775 1198.88 101.169L1166.17 119.111C1156.53 103.098 1143.12 95.0913 1125.95 95.0913C1112.83 95.0913 1102.12 99.4323 1093.82 108.114C1085.34 116.603 1081.09 127.214 1081.09 139.948C1081.09 153.067 1085.24 163.871 1093.54 172.36C1102.02 180.849 1112.54 185.094 1125.08 185.094C1133.38 185.094 1141.29 182.875 1148.81 178.438C1156.33 174 1161.83 168.309 1165.31 161.363L1198.3 180.753C1191.54 193.487 1181.51 203.615 1168.2 211.14C1155.08 218.664 1140.42 222.426 1124.21 222.426Z"
                  fill="#7355FB"
                />
                <path
                  d="M851.154 139.947C851.154 116.602 859.064 97.0197 874.885 81.1993C890.898 64.993 911.156 56.8899 935.658 56.8899C960.16 56.8899 980.418 64.993 996.432 81.1993C1012.25 97.0197 1020.16 116.602 1020.16 139.947C1020.16 163.485 1012.25 183.164 996.432 198.984C980.418 214.998 960.16 223.004 935.658 223.004C911.156 223.004 890.898 214.998 874.885 198.984C859.064 183.164 851.154 163.485 851.154 139.947ZM904.403 172.07C912.699 180.752 923.118 185.093 935.658 185.093C947.813 185.093 958.135 180.752 966.624 172.07C975.113 163.002 979.357 152.295 979.357 139.947C979.357 127.599 975.113 116.988 966.624 108.113C958.135 99.4313 947.813 95.0904 935.658 95.0904C923.118 95.0904 912.699 99.4313 904.403 108.113C896.107 116.602 891.959 127.214 891.959 139.947C891.959 152.488 896.107 163.195 904.403 172.07Z"
                  fill="#7355FB"
                />
                <path
                  d="M725.621 5.08875C757.84 5.08875 783.018 14.736 801.154 34.0292C819.482 53.3223 828.646 79.4648 828.646 112.456C828.646 145.447 819.289 171.396 800.575 190.304C782.053 209.404 756.49 218.954 723.884 218.954H708.74V177.859H725.91C744.238 177.859 758.515 171.589 768.741 159.049C778.966 146.508 784.079 130.688 784.079 111.588C784.079 93.0663 778.966 77.5346 768.741 64.994C758.515 52.4536 744.238 46.1835 725.91 46.1835H684.816V108.018H641.985V5.08875H725.621Z"
                  fill="#7355FB"
                />
                <circle cx="666.294" cy="194.837" r="24.1165" fill="#FC9C0F" />
              </svg>
            </a>
          </header>

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              flex: 1,
              gap: 16,
              textAlign: "center",
              padding: 24,
            }}
          >
            <svg
              style={{ width: 64, height: 64, color: "#D32F2F" }}
              viewBox="0 0 24 24"
              fill="#D32F2F"
            >
              <path d="M11 15h2v2h-2zm0-8h2v6h-2zm.99-5C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8z" />
            </svg>

            <h2
              style={{
                fontSize: 22,
                fontWeight: 600,
                color: "#414046",
                margin: 0,
              }}
            >
              Ocurrió un error inesperado
            </h2>

            <p
              style={{
                fontSize: 16,
                fontWeight: 400,
                color: "#757475",
                maxWidth: 400,
                lineHeight: 1.5,
                margin: 0,
              }}
            >
              Por favor, intenta de nuevo más tarde. Si el problema
              persiste, nuestro equipo de soporte estará feliz de
              ayudarte.
            </p>

            <button
              onClick={this.handleGoHome}
              style={{
                backgroundColor: "#7355FB",
                color: "#FFFFFF",
                border: "none",
                borderRadius: 8,
                padding: "12px 32px",
                fontSize: 16,
                fontWeight: 500,
                cursor: "pointer",
                fontFamily: "'Poppins', sans-serif",
                marginTop: 8,
              }}
            >
              Volver al inicio
            </button>

            <button
              onClick={this.handleContactSupport}
              style={{
                fontSize: 16,
                fontWeight: 500,
                color: "#1b72e8",
                cursor: "pointer",
                textDecoration: "underline",
                background: "none",
                border: "none",
                padding: 0,
                fontFamily: "'Poppins', sans-serif",
              }}
            >
              Contáctanos
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
